import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { enqueueSnackbar } from 'notistack';

import { getResolvedEndpoint } from '@/api/utils/getResolvedEndpoint';
import { handleApiCall } from '@/api/utils/handleApiCall';
import { MutationStateFns } from '@/api/utils/mutation';
import { useAuth } from '@/context/AuthProvider';

import { QUERY_KEY } from './useGetGroupNegotiations';

export type ApiRequest_Delete_Group_Negotiation = {
  communityId: number;
  negotiationId: number;
};

export type ApiResponse_Delete_Group_Negotiation = {
  success: boolean;
  message: string;
};

const METHOD = 'DELETE';
const ENDPOINT = 'communities/{communityId}/group-negotiations/{negotiationId}';

export const useDeleteGroupNegotiation = (
  stateFns?: MutationStateFns<ApiResponse_Delete_Group_Negotiation>
) => {
  const { session } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: ApiRequest_Delete_Group_Negotiation) =>
      handleApiCall<ApiResponse_Delete_Group_Negotiation, Record<string, never>>(
        session,
        METHOD,
        getResolvedEndpoint(ENDPOINT, {
          communityId: params.communityId.toString(),
          negotiationId: params.negotiationId.toString(),
        })
      ),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY({ communityId: variables.communityId }),
      });
      enqueueSnackbar('Save together group deleted successfully!', { variant: 'success' });
      stateFns?.onSuccess?.(data);
    },
    onError: error => {
      enqueueSnackbar('Failed to delete save together group.', { variant: 'error' });
      stateFns?.onError?.(error as AxiosError);
    },
  });
};